import React, { useState, useRef } from 'react';
import { IconTrash, IconPlus } from '@tabler/icons-react';
// import { Input, Textarea, Select, Checkbox, Button, Table } from ''; // Import components from shadcn
import { Input } from '../ui/input';
import { Textarea } from '../ui/textarea';
import { Checkbox } from '../ui/checkbox';
import { Button } from '../ui/button';

import {
  Select,
  SelectContent,
  SelectGroup,
  SelectItem,
  SelectLabel,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"

import { Table } from '../ui/table';

const TextInputComponent = ({ item, onChange }: any) => {
  return (
    <Input
      type="text"
      placeholder="Enter value..."
      value={item?.testValue}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const LongTextInputComponent = ({ item, onChange }: any) => {
  return (
    <Textarea
      placeholder="Enter value..."
      value={item?.testValue}
      onChange={(e) => onChange(e.target.value)}
      rows={1}
    />
  );
};

const OptionsDropdownComponent = ({ item, onChange }: any) => {
  return (
    <Select value={item?.testValue} onValueChange={(value) => onChange(value)}>
      <SelectTrigger className="w-full">
        <SelectValue placeholder="Yes" />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="Yes">Yes</SelectItem>
        <SelectItem value="No">No</SelectItem>
      </SelectContent>
    </Select>
  );
};


const NumericInputComponent = ({ item, onChange }: any) => {
  return (
    <Input
      type="number"
      placeholder="Enter value..."
      value={item?.testValue}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const CheckboxComponent = ({ item, onChange }: any) => {
  return (
    <Checkbox
      checked={item?.testValue}
      onCheckedChange={(checked) => onChange(checked)}
    />
  );
};

const TableInputComponent = ({ item, onChange }: any) => {
  const [rows, setRows] = useState(item?.testValue || [[...Array(2).fill("")]]);

  const handleCellChange = (rowIndex, colIndex, value) => {
    const newRows = rows.slice();
    newRows[rowIndex][colIndex] = value;
    setRows(newRows);
    onChange(newRows);
  };

  const addRow = () => {
    setRows([...rows, [...Array(rows[0].length).fill("")]]);
  };

  const addColumn = () => {
    setRows(rows.map((row) => [...row, ""]));
  };

  const deleteRow = (rowIndex) => {
    const newRows = rows.filter((_, index) => index !== rowIndex);
    setRows(newRows);
    onChange(newRows);
  };

  const deleteColumn = (colIndex) => {
    const newRows = rows.map((row) => row.filter((_, index) => index !== colIndex));
    setRows(newRows);
    onChange(newRows);
  };

  return (
    <div className="overflow-y-auto no-scrollbar max-h-64">
      <Table>
        <thead>
          <tr>
            {rows[0].map((_, colIndex) => (
              <th key={colIndex}>
                <div className="flex items-center justify-between">
                  Column {colIndex + 1}
                  <Button variant="ghost" onClick={() => deleteColumn(colIndex)}>
                    <IconTrash size={16} />
                  </Button>
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, rowIndex) => (
            <tr key={rowIndex}>
              {row.map((cell, colIndex) => (
                <td key={colIndex}>
                  <Input
                    type="text"
                    value={cell}
                    onChange={(e) => handleCellChange(rowIndex, colIndex, e.target.value)}
                  />
                </td>
              ))}
              <Button variant="ghost" onClick={() => deleteRow(rowIndex)}>
                <IconTrash size={16} />
              </Button>
            </tr>
          ))}
        </tbody>
      </Table>
      <Button variant="ghost" onClick={addRow}>
        <IconPlus size={16} />
        Add Row
      </Button>
    </div>
  );
};

const APIKeyInputComponent = ({ item, onChange }: any) => {
  return (
    <Input
      type="password"
      placeholder="Enter API key..."
      value={item?.testValue}
      onChange={(e) => onChange(e.target.value)}
    />
  );
};

const MultipleFilesToURLsInputComponent = ({ item, onChange }: any) => {
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    const urls = files.map((file: any) => URL.createObjectURL(file));
    onChange(urls);
  };

  return <Input type="file" multiple onChange={handleFileChange} />;
};

const FileToURLInputComponent = ({ item, onChange }: any) => {
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      const url = URL.createObjectURL(file);
      onChange(url);
    }
  };

  return <Input type="file" onChange={handleFileChange} />;
};

const FileToTextInputComponent = ({ item, onChange }: any) => {
  const [dragging, setDragging] = useState(false);
  const inputFileRef = useRef<HTMLInputElement | null>(null); // Create a ref for the input element

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target) {
          onChange(event.target.result);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target) {
          onChange(event.target.result);
        }
      };
      reader.readAsText(file);
    }
  };

  const handleClick = () => {
    // Trigger the file input click event
    inputFileRef.current?.click();
  };

  return (
    <>
      <div
        className={`bg-zinc-200 rounded-lg flex justify-center items-center p-8 
        ${dragging ? 'border-4 border-blue-500' : 'border-2 border-gray-300'}`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick} 
      >
        {dragging ? 'Release to upload file' : 'Drag & Drop your files or Browse'}
        
        <input
        type="file"
        ref={inputFileRef}  // Attach the ref to the hidden input
        onChange={handleFileChange}
        className="hidden"  // Keep the input hidden
      />
      </div>

     
    </>
  );
};

const ListOfJSONsInputComponent = ({ item, onChange }: any) => {
  const [list, setList] = useState(item?.testValue || []);

  const handleAddItem = () => {
    setList([...list, {}]);
  };

  const handleChange = (index, value) => {
    const newList = list.slice();
    newList[index] = value;
    setList(newList);
    onChange(newList);
  };

  return (
    <div>
      {list.map((json, index) => (
        <Textarea
          key={index}
          value={JSON.stringify(json, null, 2)}
          onChange={(e) => handleChange(index, JSON.parse(e.target.value))}
          rows={6}
        />
      ))}
      <Button variant="ghost" onClick={handleAddItem}>
        <IconPlus size={16} />
        Add JSON
      </Button>
    </div>
  );
};

const JSONInputComponent = ({ item, onChange }: any) => {
  const handleChange = (e) => {
    try {
      const parsed = JSON.parse(e.target.value);
      onChange(parsed);
    } catch (error) {
      // Handle error
    }
  };

  return (
    <Textarea
      value={JSON.stringify(item?.testValue, null, 2)}
      onChange={handleChange}
      rows={6}
    />
  );
};

const TextListInputComponent = ({ item, onChange }: any) => {
  const [list, setList] = useState(item?.testValue || []);

  const handleAddItem = () => {
    setList([...list, ""]);
  };

  const handleChange = (index, value) => {
    const newList = list.slice();
    newList[index] = value;
    setList(newList);
    onChange(newList);
  };

  return (
    <div>
      {list.map((text, index) => (
        <Input
          key={index}
          type="text"
          value={text}
          onChange={(e) => handleChange(index, e.target.value)}
          placeholder="Enter item..."
        />
      ))}
      <Button variant="ghost" onClick={handleAddItem}>
        <IconPlus size={16} />
        Add Item
      </Button>
    </div>
  );
};

const inputConfigMap = {
  'Text Input': TextInputComponent,
  'Long Text Input': LongTextInputComponent,
  'Options dropdown': OptionsDropdownComponent,
  'Numeric input': NumericInputComponent,
  'Checkbox': CheckboxComponent,
  'Text list': TextListInputComponent,
  'JSON': JSONInputComponent,
  'List of JSONs': ListOfJSONsInputComponent,
  'File to text': FileToTextInputComponent,
  'File to URL': FileToURLInputComponent,
  'Multiple Files to URLs': MultipleFilesToURLsInputComponent,
  'API key input': APIKeyInputComponent,
  'Table': TableInputComponent,
};

export default inputConfigMap
